import { type FC, useMemo } from 'react';

import { format as formatDate } from 'date-fns';

import type { MetadataMetadataFieldSchema } from '@iconik-io/iconik-api/src/services/metadata';

import { FIELDS } from '../../constants/metadata';
import { selectDateFormat } from '../../ducks/settings/select_date_format';
import { selectDateTimeFormat } from '../../ducks/settings/select_date_time_format';
import { useAppSelector } from '../../store/react_redux_typed';
import Linkify from '../../ui/linkify/linkify';
import { isEmptyMetadataValue } from '../../utils/metadata/is_empty_metadata_value';
import { parseBooleanValue } from '../../utils/metadata/parse_boolean_value';
import Labels from '../labels/labels';
import type { Field } from './types';

import styles from './metadata_row.module.css';

type Props = {
  field: MetadataMetadataFieldSchema;
  value: unknown;
};

const MetadataRow: FC<Props> = (props) => {
  const dateFormat = useAppSelector(selectDateFormat);
  const dateTimeFormat = useAppSelector(selectDateTimeFormat);

  const { field_type, label, hide_if_not_set, options } = props.field;

  // The field's type and its value arrive separately, one off the view schema
  // as a plain string and the other out of the metadata bag as unknown, so
  // nothing ties the two together until the switch below reads them as a pair.
  // biome-ignore lint/plugin: see above.
  const metadataFieldValue = {
    value: props.value,
    label,
    field_type,
  } as Field;

  const dropDownOptions = useMemo(() => {
    const fieldOptions = metadataFieldValue.value;
    if (
      !fieldOptions ||
      !options ||
      metadataFieldValue.field_type !== FIELDS.DROPDOWN
    ) {
      return [];
    }

    const optionsMap = Object.fromEntries(
      options.map((option) => [option.value, option.label]),
    );

    const dropDownOptionList = metadataFieldValue.value.map((item, i) => {
      const option = optionsMap[item] ?? item;

      return `${option}${i === fieldOptions.length - 1 ? '' : ', '}`;
    });
    return dropDownOptionList;
  }, [metadataFieldValue.value, options, metadataFieldValue.field_type]);

  if (hide_if_not_set && isEmptyMetadataValue(props.value)) {
    return null;
  }
  const metadataValue = metadataFieldValue.value?.[0];
  switch (metadataFieldValue.field_type) {
    case FIELDS.DATETIME:
      return (
        <div>
          <span className={styles.label}>{label}</span>:{' '}
          {metadataValue
            ? formatDate(new Date(metadataFieldValue.value[0]), dateTimeFormat)
            : '-'}
        </div>
      );
    case FIELDS.DATE:
      return (
        <div>
          <span className={styles.label}>{label}</span>:{' '}
          {metadataValue
            ? formatDate(new Date(metadataFieldValue.value[0]), dateFormat)
            : '-'}
        </div>
      );
    case FIELDS.DROPDOWN: {
      return (
        <div>
          <span className={styles.label}>{label}</span>:{' '}
          {dropDownOptions.length > 0
            ? dropDownOptions.map((item, i) => <span key={i}>{item}</span>)
            : '-'}
        </div>
      );
    }
    case FIELDS.STRING:
    case FIELDS.STRINGEXACT:
    case FIELDS.INTEGER:
    case FIELDS.FLOAT:
    case FIELDS.TEXT: {
      return (
        <div>
          <span className={styles.label}>{label}</span>:{' '}
          <span>{metadataFieldValue.value?.[0] || '-'}</span>
        </div>
      );
    }
    case FIELDS.BOOLEAN: {
      const isSet = !isEmptyMetadataValue(metadataFieldValue.value);
      const isTrue = parseBooleanValue(metadataValue);
      return (
        <div>
          <span className={styles.label}>{label}</span>:{' '}
          {isSet ? <span>{isTrue ? 'True' : 'False'}</span> : '-'}
        </div>
      );
    }

    case FIELDS.EMAIL:
      return (
        <div>
          <span className={styles.label}>{label}</span>:{' '}
          <Linkify forcedLabel="Email">
            {metadataFieldValue.value?.[0] || '-'}
          </Linkify>
        </div>
      );
    case FIELDS.URL: {
      return (
        <div>
          <span className={styles.label}>{label}</span>:{' '}
          <Linkify forcedLabel="Link">
            {metadataFieldValue.value?.[0] || '-'}
          </Linkify>
        </div>
      );
    }

    case FIELDS.TAGCLOUD:
      return (
        <div style={{ display: 'flex' }}>
          <span className={styles.label}>{label}</span>:{' '}
          {metadataFieldValue.value ? (
            <Labels
              className={styles.tag_label}
              labelClassName={styles.tag}
              items={metadataFieldValue.value}
              variant="gray"
            />
          ) : (
            '-'
          )}
        </div>
      );
    default:
      return null;
  }
};

export default MetadataRow;
